import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import BottomTabNavigation from '../../components/navigation/BottomTabNavigation';
import ActionButtonOverlay from '../../components/navigation/ActionButtonOverlay';
import Icon from '../../components/AppIcon';
import SummaryPreview from './components/SummaryPreview';
import ExportControls from './components/ExportControls';
import DateRangeSelector from './components/DateRangeSelector';
import QuickStats from './components/QuickStats';
import FeatureGateModal from '../feature-gate-modal';
import subscriptionService from '../../services/subscriptionService';
import symptomService from '../../services/symptomService';
import exportService from '../../services/exportService';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import UserProfileHeader from '../../components/navigation/UserProfileHeader';


import { trackDoctorReportGenerated } from '../../hooks/useGoogleAnalytics';
import appointmentService from '../../services/appointmentService';




const DoctorSummaryExport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [showFeatureGate, setShowFeatureGate] = useState(false);
  const [featureAccess, setFeatureAccess] = useState({ hasAccess: true, limit: 0, used: 0 });
  const [showEncryptionDialog, setShowEncryptionDialog] = useState(false);
  const [encryptionPassword, setEncryptionPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isCreatingArchive, setIsCreatingArchive] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    end: new Date()
  });

  const [summaryData, setSummaryData] = useState({
    symptoms: [],
    trends: [],
    triggers: [],
    impacts: [],
    medications: [],
    notes: [],
    rawEntries: []
  });

  const [stats, setStats] = useState({
    totalEntries: 0,
    symptomsTracked: 0,
    avgSeverity: 0,
    daysTracked: 0
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nearestAppointment, setNearestAppointment] = useState(null);

  // Check for pre-populated data from Daily Check-in
  useEffect(() => {
    if (location?.state?.fromCheckIn && location?.state?.prePopulatedEntry) {
      const entry = location?.state?.prePopulatedEntry;
      // Set date range to just the check-in day
      const checkInDate = new Date(entry?.timestamp || Date.now());
      const startOfDay = new Date(checkInDate);
      startOfDay?.setHours(0, 0, 0, 0);
      const endOfDay = new Date(checkInDate);
      endOfDay?.setHours(23, 59, 59, 999);
      
      setDateRange({
        start: startOfDay,
        end: endOfDay
      });
    }
  }, [location?.state]);

  useEffect(() => {
    checkExportAccess();
    if (user?.id) {
      appointmentService?.getNearestAppointment(user?.id)?.then(appt => setNearestAppointment(appt));
    }
  }, [user?.id]);

  useEffect(() => {
    if (user) {
      fetchSymptomData();
    }
  }, [user, dateRange]);

  const fetchSymptomData = async () => {
    setLoading(true);
    setError(null);

    const { data, error: fetchError } = await symptomService?.getEntries({
      dateRange: {
        start: dateRange?.start?.toISOString(),
        end: dateRange?.end?.toISOString()
      }
    });

    if (fetchError) {
      setError('Failed to load symptom data');
      setSummaryData({
        symptoms: [],
        trends: [],
        triggers: [],
        impacts: [],
        medications: [],
        notes: [],
        rawEntries: []
      });
      setStats({
        totalEntries: 0,
        symptomsTracked: 0,
        avgSeverity: 0,
        daysTracked: 0
      });
    } else {
      const entries = data || [];
      processSummaryData(entries);
    }

    setLoading(false);
    // Fire doctor_report_generated when report data loads successfully
    if (!fetchError) {
      trackDoctorReportGenerated('doctor_summary_export');
    }
  };

  const processSummaryData = (entries) => {
    if (entries?.length === 0) {
      setSummaryData({
        symptoms: [],
        trends: [],
        triggers: [],
        impacts: [],
        medications: [],
        notes: [],
        rawEntries: []
      });
      setStats({
        totalEntries: 0,
        symptomsTracked: 0,
        avgSeverity: 0,
        daysTracked: 0
      });
      return;
    }

    // Calculate symptoms summary
    const symptomMap = new Map();
    entries?.forEach(entry => {
      const categories = entry?.symptom_categories || [];
      const locations = entry?.body_locations || [];
      const severity = entry?.severity_numeric || 0;

      categories?.forEach((category, index) => {
        const location = locations?.[index] || 'Not specified';
        const key = `${category}-${location}`;
        
        if (!symptomMap?.has(key)) {
          symptomMap?.set(key, {
            name: category,
            location: location,
            severities: [],
            frequency: 0,
            durations: []
          });
        }
        
        const symptom = symptomMap?.get(key);
        symptom?.severities?.push(severity);
        symptom.frequency += 1;
        if (entry?.timing) {
          symptom?.durations?.push(entry?.timing);
        }
      });
    });

    const symptoms = Array?.from(symptomMap?.values())?.map(symptom => ({
      name: symptom?.name,
      location: symptom?.location,
      avgSeverity: symptom?.severities?.length > 0 
        ? Math?.round((symptom?.severities?.reduce((a, b) => a + b, 0) / symptom?.severities?.length) * 10) / 10
        : 0,
      frequency: symptom?.frequency,
      duration: symptom?.durations?.[0] || 'Varies'
    }));

    // Calculate trends (simplified - comparing first half vs second half)
    const trends = symptoms?.map(symptom => {
      const midpoint = Math?.floor(entries?.length / 2);
      const firstHalf = entries?.slice(0, midpoint);
      const secondHalf = entries?.slice(midpoint);

      const firstAvg = firstHalf?.filter(e => e?.symptom_categories?.includes(symptom?.name))
        ?.reduce((sum, e) => sum + (e?.severity_numeric || 0), 0) / (firstHalf?.length || 1);
      const secondAvg = secondHalf?.filter(e => e?.symptom_categories?.includes(symptom?.name))
        ?.reduce((sum, e) => sum + (e?.severity_numeric || 0), 0) / (secondHalf?.length || 1);

      const change = secondAvg - firstAvg;
      let direction = 'stable';
      let percentage = 50;

      if (change > 0.5) {
        direction = 'increasing';
        percentage = Math?.min(100, 50 + (change * 10));
      } else if (change < -0.5) {
        direction = 'decreasing';
        percentage = Math?.max(0, 50 + (change * 10));
      }

      return {
        symptom: symptom?.name,
        change: direction?.charAt(0)?.toUpperCase() + direction?.slice(1),
        direction,
        percentage: Math?.round(percentage)
      };
    });

    // Aggregate triggers
    const triggerSet = new Set();
    entries?.forEach(entry => {
      (entry?.triggers || [])?.forEach(trigger => triggerSet?.add(trigger));
      if (entry?.custom_trigger) triggerSet?.add(entry?.custom_trigger);
    });
    const triggers = Array?.from(triggerSet);

    // Aggregate daily impacts
    const impactMap = new Map();
    entries?.forEach(entry => {
      (entry?.daily_impacts || [])?.forEach(impact => {
        if (!impactMap?.has(impact)) {
          impactMap?.set(impact, []);
        }
        impactMap?.get(impact)?.push(entry?.severity_numeric || 5);
      });
    });

    const impacts = Array?.from(impactMap?.entries())?.map(([category, severities]) => ({
      category,
      score: severities?.length > 0 
        ? Math?.round((severities?.reduce((a, b) => a + b, 0) / severities?.length) * 10) / 10
        : 0
    }));

    // Aggregate medication status
    const medicationMap = new Map();
    entries?.forEach(entry => {
      const status = entry?.medication_status || 'Not specified';
      medicationMap?.set(status, (medicationMap?.get(status) || 0) + 1);
    });

    const medications = Array?.from(medicationMap?.entries())?.map(([status, count]) => ({
      status,
      count
    }));

    // Extract notes with dates
    const notes = entries
      ?.filter(entry => entry?.notes && entry?.notes?.trim()?.length > 0)
      ?.map(entry => ({
        date: new Date(entry?.created_at),
        content: entry?.notes
      }))
      ?.sort((a, b) => b?.date - a?.date)
      ?.slice(0, 10);

    // Build enriched raw entries for detailed per-entry display
    const rawEntries = entries?.map(entry => ({
      id: entry?.id,
      date: entry?.created_at,
      feelingLevel: entry?.feeling_level,
      symptomCategories: entry?.symptom_categories || [],
      otherSymptomText: entry?.other_symptom_text || '',
      bodyLocations: entry?.body_locations || [],
      otherLocationText: entry?.other_location_text || '',
      severityNumeric: entry?.severity_numeric,
      timing: entry?.timing,
      triggers: entry?.triggers || [],
      customTrigger: entry?.custom_trigger || '',
      dailyImpacts: entry?.daily_impacts || [],
      medicationStatus: entry?.medication_status || '',
      medicationDetails: entry?.medication_details || '',
      notes: entry?.notes || '',
      photos: entry?.photos || []
    }));

    setSummaryData({
      symptoms,
      trends,
      triggers,
      impacts,
      medications,
      notes,
      rawEntries
    });

    // Calculate stats
    const uniqueDays = new Set(
      entries?.map(entry => new Date(entry?.created_at)?.toDateString())
    );

    const totalSeverity = entries?.reduce((sum, entry) => sum + (entry?.severity_numeric || 0), 0);
    const avgSeverity = entries?.length > 0 ? totalSeverity / entries?.length : 0;

    const uniqueSymptoms = new Set();
    entries?.forEach(entry => {
      (entry?.symptom_categories || [])?.forEach(cat => uniqueSymptoms?.add(cat));
    });

    setStats({
      totalEntries: entries?.length,
      symptomsTracked: uniqueSymptoms?.size,
      avgSeverity: Math?.round(avgSeverity * 10) / 10,
      daysTracked: uniqueDays?.size
    });
  };

  const checkExportAccess = async () => {
    const access = await subscriptionService?.checkFeatureAccess('monthly_exports');
    setFeatureAccess(access);
  };

  const handleExportAttempt = () => {
    if (!featureAccess?.hasAccess) {
      setShowFeatureGate(true);
      return false;
    }
    return true;
  };

  const handleUpgrade = (planType) => {
    navigate('/subscription-management');
  };

  const handleExport = (format) => {
    console.log(`Exporting as ${format}`);
  };

  const handleShare = (platform) => {
    console.log(`Sharing to ${platform}`);
  };

  const handleEncryptedExport = () => {
    setShowEncryptionDialog(true);
    setEncryptionPassword('');
    setConfirmPassword('');
    setPasswordError('');
  };

  const validateEncryptionPassword = () => {
    if (!encryptionPassword) {
      setPasswordError('Password is required');
      return false;
    }

    if (encryptionPassword?.length < 12) {
      setPasswordError('Password must be at least 12 characters');
      return false;
    }

    if (encryptionPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return false;
    }

    const hasUpperCase = /[A-Z]/?.test(encryptionPassword);
    const hasLowerCase = /[a-z]/?.test(encryptionPassword);
    const hasNumbers = /[0-9]/?.test(encryptionPassword);
    const hasSpecialChars = /[!@#$%^&*(),.?":{}|<>]/?.test(encryptionPassword);

    if (!hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChars) {
      setPasswordError('Password must contain uppercase, lowercase, numbers, and special characters');
      return false;
    }

    return true;
  };

  const createEncryptedArchive = async () => {
    if (!validateEncryptionPassword()) {
      return;
    }

    setIsCreatingArchive(true);
    setPasswordError('');

    try {
      const exportData = {
        symptoms: summaryData?.symptoms,
        trends: summaryData?.trends,
        triggers: summaryData?.triggers,
        impacts: summaryData?.impacts,
        medications: summaryData?.medications,
        notes: summaryData?.notes,
        dateRange: {
          start: dateRange?.start?.toISOString(),
          end: dateRange?.end?.toISOString()
        },
        stats,
        exportedBy: user?.email,
        exportedAt: new Date()?.toISOString()
      };

      const filename = `health-export-${dateRange?.start?.toISOString()?.split('T')?.[0]}-to-${dateRange?.end?.toISOString()?.split('T')?.[0]}`;

      const { data, error } = await exportService?.createEncryptedExport(
        exportData,
        encryptionPassword,
        filename
      );

      if (error) {
        if (error?.code === 'FEATURE_LIMIT_REACHED') {
          setShowFeatureGate(true);
          setShowEncryptionDialog(false);
        } else {
          setPasswordError(error?.message || 'Failed to create encrypted archive');
        }
        return;
      }

      // Create downloadable files
      const encryptedBlob = new Blob([data?.encrypted], { type: 'application/octet-stream' });
      const metadataBlob = new Blob([JSON.stringify(data?.metadata, null, 2)], { type: 'application/json' });

      // Download encrypted data file
      const encryptedUrl = URL.createObjectURL(encryptedBlob);
      const encryptedLink = document.createElement('a');
      encryptedLink.href = encryptedUrl;
      encryptedLink.download = `${filename}.encrypted`;
      encryptedLink?.click();
      URL.revokeObjectURL(encryptedUrl);

      // Download metadata file
      const metadataUrl = URL.createObjectURL(metadataBlob);
      const metadataLink = document.createElement('a');
      metadataLink.href = metadataUrl;
      metadataLink.download = `${filename}.metadata.json`;
      metadataLink?.click();
      URL.revokeObjectURL(metadataUrl);

      setShowEncryptionDialog(false);
      alert('Encrypted archive created successfully! Two files have been downloaded: the encrypted data and metadata file. Keep both files and your password safe.');
    } catch (error) {
      console.error('Archive creation error:', error);
      setPasswordError('Failed to create encrypted archive. Please try again.');
    } finally {
      setIsCreatingArchive(false);
    }
  };

  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
  };

  return (
    <>
      <Helmet>
        <title>Doctor Summary Export - MyBodyToday</title>
        <meta name="description" content="Generate and export comprehensive health summary reports for medical consultations" />
      </Helmet>
      <div className="min-h-screen bg-gray-50 pb-24">
        <BottomTabNavigation />
        <ActionButtonOverlay />
        <UserProfileHeader />

        {/* Appointment shortcut banner */}
        {nearestAppointment && (() => {
          const apptDate = new Date(nearestAppointment?.appointment_date);
          const diffDays = Math.ceil((apptDate?.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
          return (
            <div className="max-w-4xl mx-auto px-4 pt-4">
              <div
                className="flex items-center gap-3 p-3 bg-primary/10 border border-primary/20 rounded-xl cursor-pointer hover:bg-primary/15 transition-colors"
                onClick={() => navigate('/doctor-appointments')}
              >
                <Icon name="Stethoscope" size={16} className="text-primary flex-shrink-0" />
                <p className="text-xs text-foreground flex-1">
                  <span className="font-medium">Appointment with {nearestAppointment?.doctor_name}</span>
                  {' '}in <span className="font-semibold text-primary">{diffDays} day{diffDays !== 1 ? 's' : ''}</span>
                </p>
                <Button
                  variant="default"
                  size="sm"
                  onClick={(e) => { e?.stopPropagation(); navigate('/doctor-appointments'); }}
                  className="text-xs py-1 px-3"
                >
                  Manage
                </Button>
              </div>
            </div>
          );
        })()}

        {/* Prepare for Doctor Visit - Prominent Button Row */}
        <div className="bg-indigo-50 border-b border-indigo-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <button
              onClick={() => navigate('/prepare-for-doctor-visit', { state: { summaryData, dateRange, stats } })}
              className="w-full flex items-center justify-center gap-2.5 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition-colors shadow-sm text-base"
            >
              <Icon name="Stethoscope" size={20} />
              Prepare for Doctor Visit
            </button>
          </div>
        </div>

        <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading your symptom data...</p>
              </div>
            </div>
          ) : error ? (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 text-center">
              <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
              <p className="text-destructive font-semibold mb-2">Failed to Load Data</p>
              <p className="text-muted-foreground text-sm">{error}</p>
            </div>
          ) : (
            <div className="space-y-6 md:space-y-8">
              <QuickStats stats={stats} />

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
                <div className="lg:col-span-2 space-y-6 md:space-y-8">
                  <DateRangeSelector
                    dateRange={dateRange}
                    onDateRangeChange={handleDateRangeChange}
                  />

                  <SummaryPreview
                    summaryData={summaryData}
                    dateRange={dateRange}
                  />
                </div>

                <div className="lg:col-span-1">
                  <div className="lg:sticky lg:top-24 space-y-6 md:space-y-8">
                    <ExportControls
                      onExport={handleExport}
                      onShare={handleShare}
                      onEncryptedExport={handleEncryptedExport}
                      summaryData={summaryData}
                      dateRange={dateRange}
                    />

                    <div className="bg-card rounded-xl border border-border shadow-therapeutic p-4 md:p-6">
                      <div className="flex items-start gap-3 mb-4">
                        <Icon name="Lightbulb" size={20} color="var(--color-warning)" strokeWidth={2} className="flex-shrink-0 mt-0.5" />
                        <div>
                          <h3 className="text-sm md:text-base font-semibold text-foreground mb-2">
                            Tips for Sharing Your Report
                          </h3>
                          <ul className="space-y-2 text-xs md:text-sm text-muted-foreground">
                            <li className="flex items-start gap-2">
                              <span className="text-primary mt-0.5">•</span>
                              <span>Print or email this summary before your appointment</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-primary mt-0.5">•</span>
                              <span>Highlight any concerning patterns or changes you've noticed</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-primary mt-0.5">•</span>
                              <span>Bring a list of current medications and supplements</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-primary mt-0.5">•</span>
                              <span>Write down any questions you want to ask your provider</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>

        <FeatureGateModal
          isOpen={showFeatureGate}
          onClose={() => setShowFeatureGate(false)}
          featureName="monthly_exports"
          featureDescription="Doctor Summary Export"
          currentUsage={featureAccess?.used}
          limit={featureAccess?.limit}
          onUpgrade={handleUpgrade}
        />

        {showEncryptionDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-2xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-foreground">Create Encrypted Archive</h3>
                <button
                  onClick={() => setShowEncryptionDialog(false)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Icon name="X" size={24} />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <Icon name="Lock" size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-blue-800">
                      <p className="font-medium mb-1">Encrypted Health Data</p>
                      <p>Your health data will be encrypted using AES-256 encryption. You'll receive two files: encrypted data and metadata. Both are required for decryption.</p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Encryption Password
                  </label>
                  <Input
                    type="password"
                    value={encryptionPassword}
                    onChange={(e) => setEncryptionPassword(e?.target?.value)}
                    placeholder="Enter strong password (min 12 characters)"
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Confirm Password
                  </label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e?.target?.value)}
                    placeholder="Re-enter password"
                    className="w-full"
                  />
                </div>

                {passwordError && (
                  <div className="bg-error/10 border border-error/20 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <Icon name="AlertCircle" size={18} className="text-error flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-error">{passwordError}</p>
                    </div>
                  </div>
                )}

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <Icon name="AlertTriangle" size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-amber-800">
                      <p className="font-medium mb-1">Important:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>Store your password securely - it cannot be recovered</li>
                        <li>Keep both downloaded files together</li>
                        <li>Password must be 12+ characters with uppercase, lowercase, numbers, and special characters</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowEncryptionDialog(false)}
                    className="flex-1"
                    disabled={isCreatingArchive}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={createEncryptedArchive}
                    className="flex-1"
                    disabled={isCreatingArchive}
                    iconName={isCreatingArchive ? null : 'Lock'}
                  >
                    {isCreatingArchive ? 'Creating...' : 'Create Archive'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default DoctorSummaryExport;